import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useParams, useQuery } from '../../../../contexts/RouterContext';
import { useRejectSnippets, useSnippetById, useUnrejectSnippets } from '../../../../hooks/api/snippets';
import { getProjectBaseURLFromParams, nuc, openInNewTab } from '../../../../utils';
import { icons, IconText } from '../../../generic';
import FallbackImage from '../../../generic/FallbackImage';
import { RouterLink } from '../../../generic/Link';
import Loader from '../../../generic/Loader';
import Tabs, { useTabs } from '../../../generic/Tabs';
import ModalManagePackageLabels from '../../../modals/ModalManagePackageLabels';
import { useManagePackageLabelsModal } from '../../dependenciesV2/hooks/useManagePackageLabelsModal';
import Buttons from '../Drawer/Buttons';
import Code from '../Drawer/Code/Code';
import Details from '../Drawer/Details';
import Header from '../Drawer/Header';
import RejectedBanner from '../Drawer/RejectedBanner';
import useIsSnippetConversionSupported from '../hooks/useIsSnippetConversionSupported';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

import styles from './SnippetDetails.module.less';

type DrawerTab = 'Details' | 'Compare Code';

const tabIds: Readonly<{ tabIds: DrawerTab[] }> = {
  tabIds: ['Details', 'Compare Code'],
};

/**
 * Full page snippet details view - similar to IssueDetailsV2.
 * Reuses existing drawer components but in a full page layout.
 */
const FullPageSnippetDetails: VoidFunctionComponent = () => {
  const params = useParams<RevisionRouteParams & { id: string }>();
  const { id: snippetId } = params;
  const [queryParams] = useQuery<SnippetsQueryParams>();

  const userRevisionLocator = `${params.project_id}$${params.revision_id}`;

  const { tabs, activeTab } = useTabs<DrawerTab>(tabIds);
  const manageLabelsModal = useManagePackageLabelsModal();

  const { data, isLoading } = useSnippetById(userRevisionLocator, snippetId);
  const { snippet } = data || {};
  const { mutate: rejectSnippet, isPending: isRejecting } = useRejectSnippets(
    userRevisionLocator,
    { id: snippetId },
  );
  const { mutate: unrejectSnippet, isPending: isUnrejecting } = useUnrejectSnippets(
    userRevisionLocator,
    { id: snippetId },
  );

  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const selectedSnippetMatch = snippet?.matches.find(match => match.path === selectedPath);

  const isConversionPossible = useIsSnippetConversionSupported();

  const backToSnippetsURL = useMemo(() => {
    const projectBaseURL = getProjectBaseURLFromParams(params);
    return `${projectBaseURL}/browse/snippets`;
  }, [params]);

  if (isLoading || !snippetId) {
    return (
      <div className="container container-responsive">
        <div className={styles.body}>
          <Loader />
        </div>
      </div>
    );
  }

  if (!snippet) {
    return (
      <div className="container container-responsive">
        <div className={styles.backToLink}>
          <RouterLink to={backToSnippetsURL}>
            <IconText
              icon={icons.angleLeft}
              text="Back to Snippets"
            />
          </RouterLink>
        </div>
        <div className={styles.body}>
          <FallbackImage
            src="no-results"
            alt="Snippet not found"
            hasPadding
          >
            Snippet Not Found
          </FallbackImage>
        </div>
      </div>
    );
  }

  return (
    <div className="container container-responsive">
      <div className={styles.backToLink}>
        <RouterLink to={backToSnippetsURL}>
          <IconText
            icon={icons.angleLeft}
            text="Back to Snippets"
          />
        </RouterLink>
      </div>
      <div className={styles.body}>
        <Header snippet={snippet} />
        <Buttons
          isRejected={!!snippet.rejectionDetails}
          isRejecting={isRejecting}
          onRejectClick={() => rejectSnippet({
            ids: [snippet.id],
            path: queryParams.path,
          })}
          isConversionPossible={isConversionPossible}
          onConvertClick={() => {
            // Initial version of convert click does nothing!
            // eslint-disable-next-line no-console
            console.log(`Converting: ${snippet.locator} to a vendored dependency`);
          }}
          isUnrejecting={isUnrejecting}
          onUnrejectClick={() => unrejectSnippet({
            ids: [snippet.id],
            path: queryParams.path,
          })}
          onDownloadClick={() => {
            return openInNewTab(`/api/revisions/${nuc(userRevisionLocator)}/snippets/${nuc(snippet.id)}`);
          }}
        />
        {/* When NOT comparing code, use the snippet's rejection details */}
        {activeTab !== 'Compare Code' && snippet?.rejectionDetails && (
        <RejectedBanner rejectionDetails={snippet.rejectionDetails} rejectionTarget="snippet" />
        )}
        {/* When comparing code, use the selected snippet match's rejection details */}
        {activeTab === 'Compare Code' && selectedSnippetMatch?.rejectionDetails && (
        <RejectedBanner rejectionDetails={selectedSnippetMatch.rejectionDetails} rejectionTarget="match" />
        )}
        <Tabs>
          {tabs.map(tab => (
            <Tabs.Tab
              key={tab.label}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...tab}
            />
          ))}
        </Tabs>
        {activeTab === 'Details' ? (
          <Details snippet={snippet} />
        ) : (
          <div className={styles.codeSection}>
            <Code
              userRevisionLocator={userRevisionLocator}
              selectedSnippetId={snippetId}
              matches={snippet.matches}
              selectedPath={selectedPath}
              setSelectedPath={setSelectedPath}
            />
          </div>
        )}
      </div>
      <ModalManagePackageLabels
        context={manageLabelsModal.context}
        show={manageLabelsModal.isOpen}
        onHide={manageLabelsModal.onClose}
        parentRevisionLocator={userRevisionLocator}
        isGlobalEnabled
        isProjectEnabled
      />
    </div>
  );
};

export default FullPageSnippetDetails;
